import { useEffect, useRef, useState } from "react";

/**
 * The muted, looping video behind the home and Mar heroes.
 *
 * Oct 25 (backlog 38542133, "why is there a play button?"): phones only start
 * a video on their own when it is muted, and React sets `muted` as a property,
 * never as an attribute, so the prerendered page shipped a video that was not
 * muted yet. iPhones in Low Power Mode refuse even a muted one and draw a play
 * button over it. So we mute the element ourselves, start it, and if the phone
 * still says no, show the still photo (or nothing over the dark background).
 */
const HeroVideo = ({ src, poster, className }: { src: string; poster?: string; className?: string }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute("muted", "");
    try {
      const started = el.play();
      // Only a refusal counts; an interrupted start (AbortError) is not one.
      if (started && typeof started.catch === "function") {
        started.catch((e: unknown) => {
          if ((e as { name?: string } | null)?.name === "NotAllowedError") setRefused(true);
        });
      }
    } catch {
      // No media support (tests, very old browsers): leave the element as is.
    }
  }, [src]);

  if (refused) return poster ? <img src={poster} alt="" aria-hidden="true" className={className} /> : null;
  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      disablePictureInPicture
      aria-hidden="true"
      className={`hero-video ${className ?? ""}`}
      data-testid="hero-video"
    />
  );
};

export default HeroVideo;
